/**
 * FactionScene.ts — Faction reputation display.
 *
 * Shows all 3 factions with current reputation, standing, and titles.
 * Accessed from CharacterScene.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { GameState } from '@core/engine/GameState';
import type { HintTriggerType } from '@ui/HintManager';

export class FactionScene implements Scene {
  name = 'FactionScene';

  async enter(engine: GameEngine): Promise<void> {
    const { gameState } = engine.getContext();
    if (!gameState) {
      await engine.popScene();
      return;
    }

    await this.showFactions(engine, gameState);
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  private async showFactions(engine: GameEngine, state: GameState): Promise<void> {
    const ctx = engine.getContext();
    const { effects, factionSystem, frameRenderer, terminalFrame, hintManager } = ctx;

    // Update terminal frame
    terminalFrame.updateInfo({
      chapter: 'CHARACTER',
      location: 'Factions',
    });

    ctx.sprites.hide();
    const statuses = factionSystem.getAllFactionStatuses(state);

    // Find dominant faction (only if one clearly leads)
    const maxRep = Math.max(...statuses.map(f => f.reputation));
    const topFactions = statuses.filter(f => f.reputation === maxRep);
    const hasDominant = topFactions.length === 1 && maxRep !== 0;
    const footer = hasDominant
      ? `Dominant: ${topFactions[0].name}  |  ${statuses.length} Factions`
      : `No Dominant Faction  |  ${statuses.length} Factions`;

    const layout = frameRenderer.drawFrame({
      title: 'FACTIONS',
      footer,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);

    // Trigger faction hint on first visit
    await hintManager.maybeShowHint('first_faction' as HintTriggerType, writer);

    writer.blankLine();

    for (const faction of statuses) {
      writer.writeLine(`  ${faction.symbol} ${faction.name}`, faction.color);

      // Reputation bar
      const barWidth = 20;
      const normalized = (faction.reputation + 100) / 200; // 0 to 1
      const filled = Math.round(normalized * barWidth);
      const bar = '\u2588'.repeat(filled) + '\u2591'.repeat(barWidth - filled);
      writer.writeLine(`    Rep: ${faction.reputation} [${bar}]`, 'white');
      writer.writeLine(`    Standing: ${faction.standing}`, this.getStandingColor(faction.standing));

      if (faction.title) {
        writer.writeLine(`    Title: "${faction.title}"`, 'yellow');
      }

      writer.blankLine();
    }

    await writer.pressAnyKey();
    await engine.popScene();
  }

  private getStandingColor(standing: string): 'green' | 'frostSilver' | 'white' | 'yellow' | 'red' {
    switch (standing) {
      case 'Revered': return 'green';
      case 'Friendly': return 'frostSilver';
      case 'Neutral': return 'white';
      case 'Unfriendly': return 'yellow';
      case 'Hostile': return 'red';
      default: return 'white';
    }
  }
}
